import type { I18n } from '@learnforge/i18n';

export interface CreateStyleRowOptions {
  labelKey: string;
  i18n: I18n<string>;
  color: string;
  opacity: number;
  onColorChange(hex: string): void;
  onOpacityChange(v: number): void;
}

export interface StyleRowHandle {
  element: HTMLElement;
}

// Each row needs unique ids so the visible label can associate with the color input via
// `for`. Monotonic counter keeps ids stable within a single module load.
let styleRowSeq = 0;

export function createStyleRow(opts: CreateStyleRowOptions): StyleRowHandle {
  const seq = ++styleRowSeq;
  const colorId = `style-color-${seq}`;
  const opacityId = `style-opacity-${seq}`;

  const row = document.createElement('div');
  row.className = 'style-row';
  row.innerHTML = `
    <label class="style-label" for="${colorId}"></label>
    <input type="color" class="color-input" id="${colorId}" />
    <input type="range" class="dim-slider" id="${opacityId}" min="0.05" max="1" step="any" />
  `;
  const labelEl = row.querySelector('.style-label') as HTMLLabelElement;
  const color = row.querySelector('input[type="color"]') as HTMLInputElement;
  const slider = row.querySelector('input[type="range"]') as HTMLInputElement;

  const setLabels = () => {
    const text = opts.i18n.t(opts.labelKey);
    labelEl.textContent = text;
    // The opacity slider can't share the visible label (one <label for=...> only points at
    // one control), so it gets the same translated text via aria-label. Screen readers will
    // announce it as "<label text>" — not differentiated from the color input, but better
    // than no accessible name at all.
    slider.setAttribute('aria-label', text);
  };
  setLabels();
  color.value = opts.color;
  slider.value = String(opts.opacity);

  color.addEventListener('input', () => opts.onColorChange(color.value));
  slider.addEventListener('input', () => opts.onOpacityChange(parseFloat(slider.value)));

  opts.i18n.subscribe(setLabels);

  return { element: row };
}
